
import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { useGym } from '@/contexts/gym-context';
import { apiRequest } from '@/lib/api-client';
import { toast } from '@/hooks/use-toast';
import { useQueryClient } from '@tanstack/react-query';

interface SearchUser {
  _id?: string;
  id?: string;
  name: string;
  surname?: string;
  email: string;
  phone?: string;
  status?: string;
}

interface UserSearchProps {
  onUserSelect: (user: SearchUser) => void;
}

export function UserSearch({ onUserSelect }: UserSearchProps) {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [results, setResults] = useState<SearchUser[]>([]);
  const [checkingInUser, setCheckingInUser] = useState<string | null>(null);
  const searchRef = useRef<HTMLDivElement | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { selectedGym } = useGym();
  const queryClient = useQueryClient();

  // Debounced search — fires 300ms after the user stops typing (min 2 chars)
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    const trimmed = searchTerm.trim();
    if (trimmed.length < 2) {
      setResults([]);
      setIsOpen(false);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      setIsSearching(true);
      try {
        const params = new URLSearchParams({
          userType: 'user',
          search: trimmed,
          limit: '5',
          page: '1',
        });
        const response = await apiRequest({
          method: 'GET',
          url: `/users?${params.toString()}`,
        });
        const users: SearchUser[] = response?.results ?? (Array.isArray(response) ? response : []);
        setResults(users.slice(0, 5));
        setIsOpen(users.length > 0);
      } catch {
        setResults([]);
        setIsOpen(false);
      } finally {
        setIsSearching(false);
      }
    }, 300);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchTerm]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCheckIn = async (user: SearchUser) => {
    if (!selectedGym) {
      toast({ title: 'Error', description: 'No gym selected', variant: 'destructive' });
      return;
    }

    const userId = user._id ?? user.id ?? '';
    setCheckingInUser(userId);

    try {
      await apiRequest({
        method: 'POST',
        url: '/webhook/manual',
        data: { gymId: selectedGym, userId, type: 'in' },
      });

      await queryClient.invalidateQueries({ queryKey: ['dashboardData', selectedGym] });

      toast({ title: 'Success', description: `${user.name} checked in successfully` });
      onUserSelect(user);
      setSearchTerm('');
      setIsOpen(false);
    } catch (error) {
      console.error('Check-in failed:', error);
      toast({
        title: 'Error',
        description: 'Failed to check in user. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setCheckingInUser(null);
    }
  };

  return (
    <div ref={searchRef} className="relative w-full max-w-md">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          type="text"
          placeholder={t('header.searchUsers')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-9"
        />
        {isSearching && (
          <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />
        )}
      </div>

      {isOpen && results.length > 0 && (
        <div className="absolute top-full left-0 right-0 z-50 mt-1 bg-background border rounded-md shadow-lg max-h-[300px] overflow-y-auto">
          {results.map((user) => {
            const uid = user._id ?? user.id ?? '';
            return (
              <div
                key={uid}
                className="flex items-center gap-3 p-3 hover:bg-muted border-b last:border-b-0"
              >
                <Avatar className="h-8 w-8">
                  <AvatarFallback className="text-xs">
                    <a href={`/users/detail?id=${uid}`}>
                      {[user.name, user.surname]
                        .filter(Boolean)
                        .map((n) => n![0])
                        .join('')
                        .toUpperCase()}
                    </a>
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium truncate">
                      <a href={`/users/detail?id=${uid}`}>
                        {user.name} {user.surname}
                      </a>
                    </p>
                    <Badge
                      variant={user.status === 'active' ? 'default' : 'secondary'}
                      className="text-xs"
                    >
                      {user.status}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="text-xs"
                  onClick={() => handleCheckIn(user)}
                  disabled={checkingInUser === uid}
                >
                  {checkingInUser === uid ? (
                    <>
                      <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                      {t('checkIn.checkingIn')}
                    </>
                  ) : (
                    t('checkIn.quickCheckIn')
                  )}
                </Button>
              </div>
            );
          })}
        </div>
      )}

      {isOpen && results.length === 0 && searchTerm.trim().length >= 2 && !isSearching && (
        <div className="absolute top-full left-0 right-0 z-50 mt-1 bg-background border rounded-md shadow-lg p-3">
          <p className="text-sm text-muted-foreground text-center">{t('header.noUsersFound')}</p>
        </div>
      )}
    </div>
  );
}
